import React, { useState, useEffect } from 'react';
import {
    Box,
    TextField,
    Typography,
    FormControl,
    FormControlLabel,
    Checkbox,
    Select,
    MenuItem,
    InputLabel,
    FormHelperText,
    Button,
    Link,
    InputAdornment,
    IconButton,
    Alert
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { useNavigate } from 'react-router-dom';
import TermsAndConditionsDialog from './TermsAndConditions';
import APIClient from '../utils/axios.util';
import { useAuth } from './Context/AuthContext';

// Validation Schema
const validationSchema = Yup.object().shape({
    name: Yup.string()
        .trim()
        .required('Name is required')
        .min(2, 'Name must be at least 2 characters'),
    companyEmail: Yup.string()
        .email('Email must be a valid email address.')
        .trim()
        .matches(/^[\w-.]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/, 'Email must be a valid email address.')
        .max(120, 'Email must be at most 120 characters long.') // Limit email length to 254 characters
        .required('Email is required.'),
    phoneNo: Yup.string()
        .required('Phone number is required')
        .matches(/^\d{10}$/, 'Phone number must be exactly 10 digits'),
    password: Yup.string()
        .matches(/^\S*$/, 'Password must not contain spaces.') // No spaces allowed
        .matches(/^[^A-Z]*[A-Z][^A-Z]*$/, 'Password must contain exactly one uppercase letter.') // Exactly one uppercase letter
        .matches(/^(?=.*[a-z])/, 'Password must contain at least one lowercase letter.') // At least one lowercase letter
        .matches(/^(?=.*\d)/, 'Password must contain at least one number.') // At least one number
        .matches(/^(?=.*[!@#$%^&*])/, 'Password must contain at least one special character.') // At least one special character
        .min(8, 'Password must be at least 8 characters long.') // Minimum password length of 8 characters
        .max(20, 'Password must be at most 20 characters long.')
        .required('Password is required.'),
    designation: Yup.string().notRequired(),
    domain: Yup.string().notRequired(),
    purposeOfDemo: Yup.string().notRequired(),
    otherPurpose: Yup.string().when('purposeOfDemo', {
        is: 'Other',
        then: (schema) => schema.required('Please specify your purpose for the demo'),
        otherwise: (schema) => schema.notRequired(),
    }),
    acceptTerms: Yup.boolean()
        .oneOf([true], 'You must accept the terms and conditions')
});

const UserRegistrationForm = ({ onLoginClick, setAlert, setAlertType, setAlertContent }) => {
    // useSourceTracker();
    // const tracking = useTrackSource();
    // useEffect(() => {
    //     console.log("Tracking Info:", tracking);
    // }, [tracking]);
    const [showTermsDialog, setShowTermsDialog] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [currentSetFieldValue, setCurrentSetFieldValue] = useState(null);
    const navigate = useNavigate();
    const { login } = useAuth();

    // OTP States
    const [otpSent, setOtpSent] = useState(false);
    const [otpValue, setOtpValue] = useState('');
    const [otpVerified, setOtpVerified] = useState(false);
    const [otpLoading, setOtpLoading] = useState(false);
    const [otpError, setOtpError] = useState('');
    const [otpSuccess, setOtpSuccess] = useState('');

    const initialValues = {
        name: '',
        companyEmail: '',
        phoneNo: '',
        password: '',
        designation: '',
        domain: '',
        purposeOfDemo: '',
        otherPurpose: '',
        acceptTerms: false
    };

    const designationOptions = [
        'CEO/Founder',
        'CTO/VP of Engineering',
        'Product Manager',
        'Engineering Manager',
        'Software Developer',
        'Data Scientist',
        'DevOps Engineer',
        'Sales Manager',
        'Marketing Manager',
        'Business Analyst',
        'Other'
    ];

    const domainOptions = ['BFSI', 'Banking'];

    const purposeOptions = [
        'Evaluate for purchase',
        'Technical integration assessment',
        'Proof of concept',
        'Competitive analysis',
        'Educational/Learning',
        'Investor/Partnership discussion',
        'Other'
    ];

    // Handle Send OTP
    const handleSendOtp = async (name, email) => {
        if (!name || !email) {
            setOtpError('Please fill in your name and email before sending OTP');
            return;
        }

        setOtpLoading(true);
        setOtpError('');
        setOtpSuccess('');

        try {
            const response = await APIClient.post('/users/send-otp', {
                fullname: name,
                email: email
            });

            if (response.data.success) {
                setOtpSent(true);
                setOtpSuccess('OTP sent successfully to your email!');
            } else {
                setOtpError(response.data.message || 'Failed to send OTP');
            }
        } catch (error) {
            console.error('❌ OTP send failed:', error);
            setOtpError(error.response?.data?.message || 'Failed to send OTP. Please try again.');
        } finally {
            setOtpLoading(false);
        }
    };

    // Handle Verify OTP
    const handleVerifyOtp = async (email) => {
        if (!otpValue || otpValue.trim().length === 0) {
            setOtpError('Please enter the OTP');
            return;
        }

        setOtpLoading(true);
        setOtpError('');
        setOtpSuccess('');

        try {
            const response = await APIClient.post('/users/validate_otp', {
                email: email,
                otp: otpValue
            });

            if (response.data.success) {
                setOtpVerified(true);
                setOtpSuccess('OTP verified successfully!');
            } else {
                setOtpError(response.data.message || 'Invalid OTP');
            }
        } catch (error) {
            console.error('❌ OTP verification failed:', error);
            setOtpError(error.response?.data?.message || 'Invalid OTP. Please try again.');
        } finally {
            setOtpLoading(false);
        }
    };

    const handleSubmit = (values, { setSubmitting, setErrors }) => {
        if (!otpVerified) {
            alert('Please verify your email with OTP before registering');
            setSubmitting(false);
            return;
        }

        console.log('RegUser :- ', values);

        const payload = {
            name: values.name,
            companyEmail: values.companyEmail,
            phoneNo: values.phoneNo,
            password: values.password,
            designation: values.designation,
            domain: values.domain,
            purposeOfDemo:
                values.purposeOfDemo === 'Other'
                    ? values.otherPurpose
                    : values.purposeOfDemo,
            acceptTerms: values.acceptTerms
        };

        APIClient.post('/auth/register', payload)
            .then((response) => {
                console.log('✅ Registration successful:', response.data);
                // Save user data to localStorage + sync AuthContext state
                login(response.data);
                setAlertContent(`Congratulations! You've registered successfully.`);
                setAlertType('success');
                setAlert(true);
                // New users always have access_status 'none' — send to demo request page
                navigate('/request-demos');
            })
            .catch((error) => {
                console.error('❌ Registration failed:', error);
                setAlertContent(error?.response?.data?.detail);
                setAlert(true);
                setAlertType('error');
            })
            .finally(() => {
                setSubmitting(false);
            });
    };

    const handleTermsLinkClick = (event, setFieldValue) => {
        event.preventDefault();
        setCurrentSetFieldValue(() => setFieldValue);
        setShowTermsDialog(true);
    };

    const handleTermsAccept = () => {
        if (currentSetFieldValue) {
            currentSetFieldValue('acceptTerms', true);
        }
        setShowTermsDialog(false);
    };

    return (
        <Box
            sx={{
                width: '100%',
                maxWidth: 650,
                maxHeight: '90vh',
                mx: 'auto',
                p: 2.5,
                borderRadius: 2,
                backgroundColor: 'background.paper',
                overflow: 'auto'
            }}
        >
            {/* Header */}
            <Typography variant="h5" gutterBottom align="center" fontWeight={600} sx={{ mb: 0.5 }}>
                Register to View Demo
            </Typography>
            <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 1.5 }}>
                Access interactive demos
            </Typography>

            <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
            >
                {({ values, errors, touched, handleChange, handleBlur, isSubmitting, setFieldValue }) => (
                    <Form>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2 }}>

                            {/* Name */}
                            <TextField
                                fullWidth
                                size="small"
                                name="name"
                                label="Name *"
                                value={values.name}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={touched.name && Boolean(errors.name)}
                                helperText={touched.name && errors.name}
                                sx={{ '& .MuiInputBase-root': { height: 40 } }}
                            />

                            {/* Company Email */}
                            <TextField
                                fullWidth
                                size="small"
                                name="companyEmail"
                                label="Company Email *"
                                value={values.companyEmail}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={touched.companyEmail && Boolean(errors.companyEmail)}
                                helperText={touched.companyEmail && errors.companyEmail}
                                sx={{ '& .MuiInputBase-root': { height: 40 } }}
                            />

                            {/* Phone */}
                            <TextField
                                fullWidth
                                size="small"
                                name="phoneNo"
                                label="Phone Number *"
                                value={values.phoneNo}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={touched.phoneNo && Boolean(errors.phoneNo)}
                                helperText={touched.phoneNo && errors.phoneNo}
                                sx={{ '& .MuiInputBase-root': { height: 40 } }}
                            />

                            {/* Password */}
                            <TextField
                                fullWidth
                                size="small"
                                type={showPassword ? 'text' : 'password'}
                                name="password"
                                label="Password *"
                                value={values.password}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={touched.password && Boolean(errors.password)}
                                helperText={touched.password && errors.password}
                                sx={{ '& .MuiInputBase-root': { height: 40 } }}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                onClick={() => setShowPassword(!showPassword)}
                                                edge="end"
                                            >
                                                {showPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }}
                            />

                            {/* Designation */}
                            <FormControl
                                fullWidth
                                size="small"
                                error={touched.designation && Boolean(errors.designation)}
                            >
                                <InputLabel>Designation</InputLabel>
                                <Select
                                    name="designation"
                                    value={values.designation}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    label="Designation"
                                    sx={{ height: 40 }}
                                >
                                    <MenuItem value="">Select your role</MenuItem>
                                    {designationOptions.map((option) => (
                                        <MenuItem key={option} value={option}>
                                            {option}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {touched.designation && errors.designation && (
                                    <FormHelperText>{errors.designation}</FormHelperText>
                                )}
                            </FormControl>

                            {/* Domain */}
                            <FormControl
                                fullWidth
                                size="small"
                                error={touched.domain && Boolean(errors.domain)}
                            >
                                <InputLabel>Domain</InputLabel>
                                <Select
                                    name="domain"
                                    value={values.domain}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    label="Domain"
                                    sx={{ height: 40 }}
                                >
                                    <MenuItem value="">Select your domain</MenuItem>
                                    {domainOptions.map((option) => (
                                        <MenuItem key={option} value={option}>
                                            {option}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {touched.domain && errors.domain && (
                                    <FormHelperText>{errors.domain}</FormHelperText>
                                )}
                            </FormControl>

                            {/* Purpose of Demo */}
                            <FormControl
                                fullWidth
                                size="small"
                                error={touched.purposeOfDemo && Boolean(errors.purposeOfDemo)}
                            >
                                <InputLabel>Purpose of Demo</InputLabel>
                                <Select
                                    name="purposeOfDemo"
                                    value={values.purposeOfDemo}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    label="Purpose of Demo"
                                    sx={{ height: 40 }}
                                >
                                    <MenuItem value="">Select purpose</MenuItem>
                                    {purposeOptions.map((option) => (
                                        <MenuItem key={option} value={option}>
                                            {option}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {touched.purposeOfDemo && errors.purposeOfDemo && (
                                    <FormHelperText>{errors.purposeOfDemo}</FormHelperText>
                                )}
                            </FormControl>

                            {/* Other Purpose - Conditional */}
                            {values.purposeOfDemo === 'Other' && (
                                <TextField
                                    fullWidth
                                    size="small"
                                    name="otherPurpose"
                                    label="Please specify"
                                    value={values.otherPurpose}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={touched.otherPurpose && Boolean(errors.otherPurpose)}
                                    helperText={touched.otherPurpose && errors.otherPurpose}
                                    sx={{ '& .MuiInputBase-root': { height: 40 } }}
                                />
                            )}

                            {/* OTP Messages */}
                            {otpError && (
                                <Alert severity="error" sx={{ py: 0.5 }}>{otpError}</Alert>
                            )}
                            {otpSuccess && (
                                <Alert severity="success" sx={{ py: 0.5 }}>{otpSuccess}</Alert>
                            )}

                            {/* OTP Input */}
                            {otpSent && !otpVerified && (
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Enter OTP"
                                    value={otpValue}
                                    onChange={(e) => setOtpValue(e.target.value)}
                                    placeholder="Enter 6-digit OTP"
                                    disabled={otpVerified}
                                    sx={{ '& .MuiInputBase-root': { height: 40 } }}
                                />
                            )}

                            {/* OTP Button */}
                            {!otpVerified && (
                                <Button
                                    fullWidth
                                    variant="outlined"
                                    onClick={() => {
                                        if (!otpSent) {
                                            handleSendOtp(values.name, values.companyEmail);
                                        } else {
                                            handleVerifyOtp(values.companyEmail);
                                        }
                                    }}
                                    disabled={otpLoading || otpVerified}
                                    sx={{ height: 40, textTransform: 'none' }}
                                >
                                    {otpLoading ? 'Processing...' : otpSent ? 'Verify OTP' : 'Send OTP'}
                                </Button>
                            )}

                            {otpVerified && (
                                <Typography variant="body2" color="success.main" align="center" sx={{ py: 0.5 }}>
                                    ✓ Email verified successfully!
                                </Typography>
                            )}

                            {/* Terms */}
                            <Box>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={values.acceptTerms}
                                            // onChange={(e) => {
                                            //     if (!values.acceptTerms) {
                                            //         e.preventDefault();
                                            //         setCurrentSetFieldValue(() => setFieldValue);
                                            //         setShowTermsDialog(true);
                                            //     } else {
                                            //         setFieldValue('acceptTerms', false);
                                            //     }
                                            // }}
                                            onChange={(e) => {
                                                setFieldValue('acceptTerms', e.target.checked);
                                            }}
                                            disabled={isSubmitting}
                                        />
                                    }
                                    label={
                                        <Typography variant="body2">
                                            I accept the{' '}
                                            <Link
                                                component="button"
                                                onClick={(e) => handleTermsLinkClick(e, setFieldValue)}
                                                sx={{ color: 'primary.main', textDecoration: 'underline' }}
                                            >
                                                Terms & Conditions
                                            </Link>
                                            {' '}for contact and demos
                                        </Typography>
                                    }
                                    sx={{ alignItems: 'flex-start', ml: 0, mt: 0 }}
                                />
                                {touched.acceptTerms && errors.acceptTerms && (
                                    <FormHelperText error>{errors.acceptTerms}</FormHelperText>
                                )}
                            </Box>

                            {/* Submit Button */}
                            <Button
                                fullWidth
                                size="medium"
                                type="submit"
                                variant="contained"
                                disabled={isSubmitting || !otpVerified}
                                sx={{ mt: 1, height: 42, textTransform: 'none' }}
                            >
                                {isSubmitting ? 'Registering...' : 'Register & Access Demo'}
                            </Button>

                            {/* Login Link */}
                            <Typography variant="body2" align="center" sx={{ mt: 0.5 }}>
                                Have an Account?{' '}
                                <Link component="button" onClick={onLoginClick} sx={{ cursor: 'pointer' }}>
                                    Login
                                </Link>
                            </Typography>
                        </Box>
                    </Form>
                )}
            </Formik>

            {/* Terms Dialog */}
            <TermsAndConditionsDialog
                open={showTermsDialog}
                onClose={() => setShowTermsDialog(false)}
                onAccept={handleTermsAccept}
            />
        </Box>
    );
};

export default UserRegistrationForm;
